/**
 * @file Quick Links.
 */

import { gsap } from 'gsap';
import { ScrollToPlugin } from 'gsap/ScrollToPlugin';

/**
 * Quick Links component.
 */
const vQuickLinks = () => {
	const quickLinks = document.querySelector('.v-quick-links');

	if (!quickLinks) {
		return;
	}

	gsap.registerPlugin(ScrollToPlugin);

	const triggers = quickLinks.querySelectorAll('.v-quick-links__trigger');
	const largeQuery = window.matchMedia('(max-width: 1280px)');

	/**
	 * Toggle Quick Links content.
	 * @param {MouseEvent} e - Click event.
	 */
	const toggleContent = (e) => {
		e.preventDefault();
		const btn = e.target.closest('.v-quick-links__trigger');
		const target = largeQuery.matches ? btn.nextElementSibling : quickLinks.querySelector( `#v-quick-links__content-block-${btn.getAttribute('data-index')}` );
		const isOpen = btn.getAttribute('aria-expanded') === 'true';

		document.dispatchEvent(
			new CustomEvent('vQuickLinksToggle', {
				detail: {
					open: !isOpen,
					target,
				},
			})
		);
	};

	/**
	 * Toggle mobile content.
	 * @param {HTMLElement} elem - Element to toggle.
	 * @param {boolean} open - Whether to open or close.
	 */
	const toggleMobile = (elem, open = true) => {
		if (elem.classList.contains('animating')) {
			return;
		}

		const trigger = elem.previousElementSibling;
		const inner = elem.querySelector('.v-quick-links__item-inner');

		if (open) {
			gsap.set(elem, { display: 'block' });
		}

		const tl = gsap.timeline({
			defaults: {
				duration: 0.35,
			},
			onStart: () => {
				elem.classList.add('animating');
			},
			onComplete: () => {
				elem.classList.remove('animating');
				elem.classList.toggle('open', open);
				trigger.setAttribute('aria-expanded', open);
				gsap.set(elem, { clearProps: 'all' });
			},
		});


		tl.to(elem, {
			height: open ? inner.getBoundingClientRect().height : 0,
		});

		if (open) {
			tl.to(window, {
				scrollTo: {
					y: `#${trigger.id}`,
					offsetY: largeQuery.matches ? 100 : 50,
				},
				ease: 'power2.inOut',
			});
		}
	};

	/**
	 * Toggle content on large screens.
	 * @param {HTMLElement} elem - Element to toggle.
	 */
	const toggleLarge = (elem) => {
		if (elem.parentElement.classList.contains('animating') || elem.classList.contains('open')) {
			return;
		}

		const trigger = quickLinks.querySelector(`[data-index="${elem.id.split('-').pop()}"]`);
		const active = elem.parentElement.querySelector('.open');
		const activeTrigger = quickLinks.querySelector(`[data-index="${active.id.split('-').pop()}"]`);
		const tl = gsap.timeline({
			defaults: {
				duration: 0.35,
			},
			onStart: () => {
				elem.parentElement.classList.add('animating');
				elem.classList.add('open');
				trigger.setAttribute('aria-expanded', true);
				activeTrigger.setAttribute('aria-expanded', false);
			},
			onComplete: () => {
				active.classList.remove('open');
				active.setAttribute('inert', 'true');
				gsap.set(active, { clearProps: 'all' });
				elem.parentElement.classList.remove('animating');
			},
		});

		tl
		.to(active, { opacity: 0 })
		.from(elem, {
			opacity: 0,
			onStart: () => {
				elem.removeAttribute('inert');
				gsap.set(elem, { clearProps: 'all' });
			}
		});
	};

	if ( triggers.length ) {
		const mobileContents = quickLinks.querySelectorAll('.v-quick-links__item-content');
		const largeContents = quickLinks.querySelectorAll('.v-quick-links__content-block');

		document.addEventListener('vQuickLinksToggle', (e) => {
			const { open, target } = e.detail;

			if ([...mobileContents].includes(target)) {
				toggleMobile(target, open);
				// maintainScrollPosition(open);
			}

			if ([...largeContents].includes(target)) {
				toggleLarge(target);
			}
		});

		triggers.forEach( ( btn ) => {
			btn.addEventListener( 'click', toggleContent );
		} );

		largeQuery.addEventListener('change', () => {
			// reset tabs
			[...mobileContents].slice(1).forEach((content) => content.classList.remove('open'));
			[...triggers].slice(1).forEach((trigger) => trigger.setAttribute('aria-expanded', false));

			mobileContents[0].classList.add('open');
			triggers[0].setAttribute('aria-expanded', true);
		});
	}
};

export default vQuickLinks;
